import { omit } from 'lodash';
import { OrganizationTypeWithLinkedProfessions } from './organizationType';

export enum Profession {
    addictionSpecialist = 'addictionSpecialist',
    assistant = 'assistant',
    audiologist = 'audiologist',
    bariNurse = 'bariNurse',
    basicPractitioner = 'basicPractitioner',
    basicPsychologist = 'basicPsychologist',
    clinicalPsychologist = 'clinicalPsychologist',
    cognitiveBehavioralTherapist = 'cognitiveBehavioralTherapist',
    contactLensSpecialist = 'contactLensSpecialist',
    dentist = 'dentist',
    dietitian = 'dietitian',
    emdrTherapist = 'emdrTherapist',
    exerciseTherapist = 'exerciseTherapist',
    functionAssistant = 'functionAssistant',
    generalPractitioner = 'generalPractitioner',
    generalPractitionerAios = 'generalPractitionerAios',
    generalPractitionerHidha = 'generalPractitionerHidha',
    generalPractitionerMentalHealthSpecialist = 'generalPractitionerMentalHealthSpecialist',
    generalPractitionerWaarnemer = 'generalPractitionerWaarnemer',
    gzPsychologist = 'gzPsychologist',
    haptotherapist = 'haptotherapist',
    hearingCareProfessional = 'hearingCareProfessional',
    hospitalSecretarialAssistant = 'hospitalSecretarialAssistant',
    imhSpecialist = 'imhSpecialist',
    laborant = 'laborant',
    lifestyleCoach = 'lifestyleCoach',
    lossMentor = 'lossMentor',
    medicalSpecialist = 'medicalSpecialist',
    nurse = 'nurse',
    nurseConsultant = 'nurseConsultant',
    nursePractitioner = 'nursePractitioner',
    nursePractitionerMentalHealth = 'nursePractitionerMentalHealth',
    nursePractitionerSomatic = 'nursePractitionerSomatic',
    nurseSpecialist = 'nurseSpecialist',
    obstetrician = 'obstetrician',
    occupationalTherapist = 'occupationalTherapist',
    optician = 'optician',
    optometrist = 'optometrist',
    optometristIo = 'optometristIo',
    orthoptist = 'orthoptist',
    osasNurse = 'osasNurse',
    other = 'other',
    outpatientAssistant = 'outpatientAssistant',
    pharmacist = 'pharmacist',
    phdStudent = 'phdStudent',
    physicianAssistant = 'physicianAssistant',
    physicianAssistantOphthalmology = 'physicianAssistantOphthalmology',
    physiotherapist = 'physiotherapist',
    podiatrist = 'podiatrist',
    practiceManager = 'practiceManager',
    psychologistSecretarialAssistant = 'psychologistSecretarialAssistant',
    psychosocialHypnotherapist = 'psychosocialHypnotherapist',
    psychotherapist = 'psychotherapist',
    relationshipAndFamilyTherapist = 'relationshipAndFamilyTherapist',
    remedialEducationalist = 'remedialEducationalist',
    resident = 'resident',
    sexologist = 'sexologist',
    skinTherapist = 'skinTherapist',
    socialPedagogicalCareProvider = 'socialPedagogicalCareProvider',
    socialPsychiatricNurses = 'socialPsychiatricNurses',
    socialWorker = 'socialWorker',
    specialistGeriatricMedicine = 'specialistGeriatricMedicine',
    speechTherapist = 'speechTherapist',
    systemTherapist = 'systemTherapist',
    technicalOphthalmicAssistant = 'technicalOphthalmicAssistant',
    teenageCoach = 'teenageCoach',
    visualTherapist = 'visualTherapist',
}

const gpPracticeProfessions: Partial<Record<Profession, string>> = {
    assistant: Profession.assistant,
    basicPractitioner: Profession.basicPractitioner,
    generalPractitioner: Profession.generalPractitioner,
    generalPractitionerAios: Profession.generalPractitionerAios,
    generalPractitionerHidha: Profession.generalPractitionerHidha,
    generalPractitionerMentalHealthSpecialist: Profession.generalPractitionerMentalHealthSpecialist,
    generalPractitionerWaarnemer: Profession.generalPractitionerWaarnemer,
    nurse: Profession.nurse,
    nursePractitionerMentalHealth: Profession.nursePractitionerMentalHealth,
    nursePractitionerSomatic: Profession.nursePractitionerSomatic,
    nurseSpecialist: Profession.nurseSpecialist,
    physicianAssistant: Profession.physicianAssistant,
    practiceManager: Profession.practiceManager,
};

export type GgzProfession = (typeof GgzProfession)[keyof typeof GgzProfession];

export const GgzProfession = {
    addictionSpecialist: Profession.addictionSpecialist,
    basicPsychologist: Profession.basicPsychologist,
    clinicalPsychologist: Profession.clinicalPsychologist,
    dietitian: Profession.dietitian,
    generalPractitionerMentalHealthSpecialist: Profession.generalPractitionerMentalHealthSpecialist,
    gzPsychologist: Profession.gzPsychologist,
    lifestyleCoach: Profession.lifestyleCoach,
    medicalSpecialist: Profession.medicalSpecialist,
    nurse: Profession.nurse,
    nurseSpecialist: Profession.nurseSpecialist,
    psychologistSecretarialAssistant: Profession.psychologistSecretarialAssistant,
    psychotherapist: Profession.psychotherapist,
    remedialEducationalist: Profession.remedialEducationalist,
    socialPedagogicalCareProvider: Profession.socialPedagogicalCareProvider,
    socialPsychiatricNurses: Profession.socialPsychiatricNurses,
    systemTherapist: Profession.systemTherapist,
} as const;

export const professionsByOrganizationType: Record<
    OrganizationTypeWithLinkedProfessions,
    Partial<Record<Profession, string>>
> = {
    [OrganizationTypeWithLinkedProfessions.addictionCareCentre]: {
        addictionSpecialist: Profession.addictionSpecialist,
        basicPractitioner: Profession.basicPractitioner,
        medicalSpecialist: Profession.medicalSpecialist,
        nurse: Profession.nurse,
        nurseSpecialist: Profession.nurseSpecialist,
    },
    [OrganizationTypeWithLinkedProfessions.centerForMedicalSpecialistCare]: {
        assistant: Profession.assistant,
        laborant: Profession.laborant,
        medicalSpecialist: Profession.medicalSpecialist,
        nurseSpecialist: Profession.nurseSpecialist,
        resident: Profession.resident,
    },
    [OrganizationTypeWithLinkedProfessions.dietitianPractice]: {
        assistant: Profession.assistant,
        dietitian: Profession.dietitian,
        lifestyleCoach: Profession.lifestyleCoach,
    },
    [OrganizationTypeWithLinkedProfessions.gpPractice]: gpPracticeProfessions,
    [OrganizationTypeWithLinkedProfessions.hospital]: {
        assistant: Profession.assistant,
        basicPractitioner: Profession.basicPractitioner,
        basicPsychologist: Profession.basicPsychologist,
        clinicalPsychologist: Profession.clinicalPsychologist,
        dietitian: Profession.dietitian,
        exerciseTherapist: Profession.exerciseTherapist,
        gzPsychologist: Profession.gzPsychologist,
        hospitalSecretarialAssistant: Profession.hospitalSecretarialAssistant,
        medicalSpecialist: Profession.medicalSpecialist,
        nurse: Profession.nurse,
        nurseConsultant: Profession.nurseConsultant,
        nurseSpecialist: Profession.nurseSpecialist,
        obstetrician: Profession.obstetrician,
        occupationalTherapist: Profession.occupationalTherapist,
        phdStudent: Profession.phdStudent,
        physicianAssistant: Profession.physicianAssistant,
        physiotherapist: Profession.physiotherapist,
        podiatrist: Profession.podiatrist,
        speechTherapist: Profession.speechTherapist,
    },
    [OrganizationTypeWithLinkedProfessions.ksyosHospital]: {
        assistant: Profession.assistant,
        audiologist: Profession.audiologist,
        bariNurse: Profession.bariNurse,
        medicalSpecialist: Profession.medicalSpecialist,
        osasNurse: Profession.osasNurse,
        outpatientAssistant: Profession.outpatientAssistant,
    },
    [OrganizationTypeWithLinkedProfessions.medicalDiagnosticCenterLaboratory]: {
        assistant: Profession.assistant,
        laborant: Profession.laborant,
    },
    [OrganizationTypeWithLinkedProfessions.nursingFacility]: {
        assistant: Profession.assistant,
        basicPractitioner: Profession.basicPractitioner,
        nurse: Profession.nurse,
        nurseSpecialist: Profession.nurseSpecialist,
        resident: Profession.resident,
        specialistGeriatricMedicine: Profession.specialistGeriatricMedicine,
    },
    [OrganizationTypeWithLinkedProfessions.optometryPractice]: {
        assistant: Profession.assistant,
        contactLensSpecialist: Profession.contactLensSpecialist,
        optician: Profession.optician,
        optometrist: Profession.optometrist,
        optometristIo: Profession.optometristIo,
        orthoptist: Profession.orthoptist,
        practiceManager: Profession.practiceManager,
    },
    [OrganizationTypeWithLinkedProfessions.psychiatryCenter]: {
        addictionSpecialist: Profession.addictionSpecialist,
        assistant: Profession.assistant,
        basicPractitioner: Profession.basicPractitioner,
        basicPsychologist: Profession.basicPsychologist,
        clinicalPsychologist: Profession.clinicalPsychologist,
        gzPsychologist: Profession.gzPsychologist,
        medicalSpecialist: Profession.medicalSpecialist,
        nurse: Profession.nurse,
        nurseSpecialist: Profession.nurseSpecialist,
        resident: Profession.resident,
    },
    [OrganizationTypeWithLinkedProfessions.psychologyPracticePsychotherapyPractice]: omit(
        GgzProfession,
        'generalPractitionerMentalHealthSpecialist'
    ),
    [OrganizationTypeWithLinkedProfessions.ggzInstitution]: omit(
        GgzProfession,
        'generalPractitionerMentalHealthSpecialist'
    ),
    [OrganizationTypeWithLinkedProfessions.independentPractice]: omit(
        GgzProfession,
        'generalPractitionerMentalHealthSpecialist'
    ),
    [OrganizationTypeWithLinkedProfessions.skinTherapyPractice]: {
        skinTherapist: Profession.skinTherapist,
    },
    [OrganizationTypeWithLinkedProfessions.testOrganization]: gpPracticeProfessions,
};
